import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { getCart, addUpdateCart, applyRemoveCoupon, cartCheckout } from "@common/utils/cart"

// Initial state
const initialState = {
  cartItems: [],
  price: {
    price: 0,
    discount: 0,
    finalPrice: 0,
    couponCode: null,
    message: "",
  },
  loading: false,
  error: null,
}

// Async thunks
export const fetchCart = createAsyncThunk("cart/fetchCart", async (_, { rejectWithValue }) => {
  try {
    const response = await getCart()
    return response?.data
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const updateCartItem = createAsyncThunk(
  "cart/updateCartItem",
  async ({ productId, quantity, type = "update", redirectToCart = false }, { rejectWithValue }) => {
    try {
      const response = await addUpdateCart(productId, quantity, type, null, redirectToCart)
      return response?.data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

export const applyCoupon = createAsyncThunk("cart/applyCoupon", async (couponCode, { rejectWithValue }) => {
  try {
    const response = await applyRemoveCoupon(couponCode)
    return response?.data
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const checkoutCart = createAsyncThunk(
  "cart/checkout",
  async ({ shippingAddressId, billingAddressId }, { rejectWithValue }) => {
    try {
      const success = await cartCheckout(shippingAddressId, billingAddressId)
      return success
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

// Cart slice
const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    clearCart: (state) => {
      state.cartItems = []
      state.price = initialState.price
    //   localStorage.removeItem("cartData")
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch cart
      .addCase(fetchCart.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false
        state.cartItems = action.payload?.cartItem || []
        state.price = action.payload?.price || initialState.price
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Update cart item
      .addCase(updateCartItem.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.loading = false
        state.cartItems = action.payload?.cartItem || []
        state.price = action.payload?.price || initialState.price
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        // Preserve the current cartItems and price
        state.cartItems = state.cartItems; // Ensure cartItems remain unchanged
        state.price = state.price; // Ensure price remains unchanged
      })

      // Apply coupon
      .addCase(applyCoupon.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(applyCoupon.fulfilled, (state, action) => {
        state.loading = false
        state.cartItems = action.payload?.cartItem || []
        state.price = action.payload?.price || initialState.price
      })
      .addCase(applyCoupon.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Checkout
      .addCase(checkoutCart.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(checkoutCart.fulfilled, (state, action) => {
        if (action.payload) {
          state.loading = false
          state.cartItems = []
          state.price = initialState.price
          localStorage.removeItem("cartData")
        }
      })
      .addCase(checkoutCart.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

// Export actions and reducer
export const { clearCart } = cartSlice.actions
export default cartSlice.reducer

// Selectors
export const selectCartItems = (state) => state.cart.cartItems
export const selectCartPrice = (state) => state.cart.price
export const selectCartLoading = (state) => state.cart.loading
export const selectCartItemCount = (state) => state.cart.cartItems.length
export const selectCartTotal = (state) => state.cart.price.finalPrice
